import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Upload, Loader2, FileText, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS_META = {
  none: { color: "text-muted-foreground", icon: ShieldCheck, en: "Not submitted", hi: "अभी सबमिट नहीं" },
  pending: { color: "text-amber-600", icon: Clock, en: "Under review", hi: "समीक्षा में" },
  verified: { color: "text-emerald-600", icon: CheckCircle2, en: "Verified", hi: "सत्यापित" },
  rejected: { color: "text-destructive", icon: XCircle, en: "Rejected", hi: "अस्वीकार" },
};

export default function KycVerify() {
  const { user, loading: authLoading } = useAuth();
  const { lang } = useLang();
  const [kyc, setKyc] = useState(null);
  const [aadhaarPath, setAadhaarPath] = useState(null);
  const [panPath, setPanPath] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/kyc/me").then(({ data }) => setKyc(data)).catch(() => setKyc({ kyc_status: "none" }));
  }, [user]);

  if (authLoading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/kyc" }} replace />;

  const upload = async (file, kind, setPath) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/paper", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPath(data.path);
      toast.success(`${kind.toUpperCase()} uploaded`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submit = async () => {
    if (!aadhaarPath && !panPath) {
      toast.error(lang === "hi" ? "कम से कम एक डॉक्युमेंट अपलोड करें" : "Upload at least one document");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/kyc/submit", {
        aadhaar_path: aadhaarPath,
        pan_path: panPath,
        note,
      });
      toast.success(lang === "hi" ? "KYC सबमिट हो गई। समीक्षा में है।" : "KYC submitted. Under review.");
      const { data } = await api.get("/kyc/me");
      setKyc(data);
      setAadhaarPath(null);
      setPanPath(null);
      setNote("");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const status = kyc?.kyc_status || "none";
  const meta = STATUS_META[status] || STATUS_META.none;
  const Icon = meta.icon;
  const canSubmit = status === "none" || status === "rejected";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <div className="overline text-secondary flex items-center gap-1.5"><ShieldCheck size={12} /> Trust & Safety</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "KYC सत्यापन" : "KYC Verification"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "hi"
            ? "वेरिफ़ाइड बैज पाएं और खरीदारों का भरोसा जीतें। आपकी KYC विवरण एन्क्रिप्टेड और गोपनीय हैं।"
            : "Earn a verified badge and win buyer trust. Your KYC details stay encrypted and private."}
        </p>
      </div>

      <Card className="p-5 mb-5" data-testid="kyc-status-card">
        <div className="flex items-center gap-3">
          <Icon size={28} className={meta.color} />
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {lang === "hi" ? "आपकी KYC स्थिति" : "Your KYC status"}
            </div>
            <div className={`text-xl font-bold ${meta.color}`}>
              {lang === "hi" ? meta.hi : meta.en}
            </div>
          </div>
        </div>
        {kyc?.kyc_submitted_at && (
          <div className="text-xs text-muted-foreground mt-3">
            {lang === "hi" ? "सबमिट किया गया:" : "Submitted on:"} {new Date(kyc.kyc_submitted_at).toLocaleString("en-IN")}
          </div>
        )}
        {status === "rejected" && kyc?.kyc_reason && (
          <div className="mt-3 p-3 bg-destructive/10 rounded-md text-xs text-destructive">
            <span className="font-semibold">{lang === "hi" ? "कारण:" : "Reason:"}</span> {kyc.kyc_reason}
          </div>
        )}
        {status === "verified" && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-800">
            {lang === "hi"
              ? "बधाई हो! आपकी लिस्टिंग पर अब \"वेरिफ़ाइड\" बैज दिखेगा।"
              : "Congratulations! Your listings now show a \"Verified\" badge."}
          </div>
        )}
      </Card>

      {canSubmit && (
        <Card className="p-5 space-y-4">
          <div className="overline text-primary">
            {lang === "hi" ? "डॉक्युमेंट अपलोड करें" : "Upload Documents"}
          </div>

          <div>
            <Label>{lang === "hi" ? "आधार कार्ड (PDF / फ़ोटो)" : "Aadhaar Card (PDF / Image)"}</Label>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="mt-2 text-sm block"
              onChange={(e) => e.target.files[0] && upload(e.target.files[0], "aadhaar", setAadhaarPath)}
              data-testid="kyc-upload-aadhaar"
            />
            {uploading.aadhaar && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline animate-spin" size={12} /> Uploading…</div>}
            {aadhaarPath && <div className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><FileText size={12} /> Aadhaar uploaded</div>}
          </div>

          <div>
            <Label>{lang === "hi" ? "पैन कार्ड (PDF / फ़ोटो)" : "PAN Card (PDF / Image)"}</Label>
            <input
              type="file"
              accept="application/pdf,image/*"
              className="mt-2 text-sm block"
              onChange={(e) => e.target.files[0] && upload(e.target.files[0], "pan", setPanPath)}
              data-testid="kyc-upload-pan"
            />
            {uploading.pan && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline animate-spin" size={12} /> Uploading…</div>}
            {panPath && <div className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><FileText size={12} /> PAN uploaded</div>}
          </div>

          <div>
            <Label>{lang === "hi" ? "टिप्पणी (ऐच्छिक)" : "Note (optional)"}</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="kyc-note" />
          </div>

          <Button
            className="w-full bg-primary hover:bg-primary/90 rounded-full"
            onClick={submit}
            disabled={submitting || (!aadhaarPath && !panPath)}
            data-testid="kyc-submit-btn"
          >
            {submitting ? <><Loader2 className="animate-spin mr-2" size={14} />{lang === "hi" ? "सबमिट हो रहा है…" : "Submitting…"}</> : <><Upload size={14} className="mr-2" />{lang === "hi" ? "समीक्षा के लिए भेजें" : "Submit for review"}</>}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            <Link to="/privacy" className="underline">{lang === "hi" ? "आपका डेटा कैसे सुरक्षित है" : "How your data stays safe"}</Link>
          </p>
        </Card>
      )}
    </div>
  );
}
